export const RENTAL_PERIOD_OPTIONS = [
  { value: 'Per Day', label: 'Per Day' },
  { value: 'Per Rental', label: 'Per Rental' },
];

export const APPLIES_ON_OPTIONS = [
  { value: 'Only General', label: 'Only General' },
  { value: 'Only Government', label: 'Only Government' },
  { value: 'Both General & Government', label: 'Both General & Government' },
];

export const selectedOption = (options, value) => (
  (options || []).find((option) => option.value === value) || null
);

export const normalizeAppliedTaxOptions = (values) => {
  const normalized = [];
  const seen = new Set();

  (Array.isArray(values) ? values : []).forEach((item) => {
    const rawValue = typeof item === 'string'
      ? item
      : item?.value ?? item?.label;
    const value = rawValue === undefined || rawValue === null
      ? ''
      : String(rawValue).trim();

    if (!value || seen.has(value)) return;

    const rawLabel = typeof item === 'object' && item !== null
      ? item.label
      : value;
    const label = String(rawLabel || value).trim() || value;

    normalized.push({ value, label });
    seen.add(value);
  });

  return normalized;
};

export const appliedTaxSummary = (values) => (
  normalizeAppliedTaxOptions(values).map((option) => option.label).join(', ')
);

export const buildTaxCopyTargetOptions = (locations, sourceLocationId) => {
  const sourceId = String(sourceLocationId || '').trim();
  const seen = new Set();

  return (Array.isArray(locations) ? locations : []).reduce((options, location) => {
    const locationId = String(
      location?.location_id ?? location?._id ?? ''
    ).trim();
    if (!locationId || locationId === sourceId || seen.has(locationId)) {
      return options;
    }

    const locationName = String(location?.location_name || '').trim();
    const separatorIndex = locationId.indexOf('-');
    const locationCode = separatorIndex >= 0
      ? locationId.slice(separatorIndex + 1)
      : locationId;

    options.push({
      value: locationId,
      label: locationName
        ? `${locationName} - ${locationCode}`
        : locationCode,
    });
    seen.add(locationId);
    return options;
  }, []);
};

export const buildTaxLocationOptions = (locations) => (
  buildTaxCopyTargetOptions(locations, '')
);

const safeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? Math.trunc(count) : 0;
};

const countLabel = (count, singular, plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
);

export const taxCopyVerificationRows = (result) => {
  const sourceCounts = result?.source_counts || {};
  const sourceTotalFromCategories = ['independent', 'baserate', 'calculated']
    .reduce((total, category) => total + safeCount(sourceCounts[category]), 0);
  const sourceTaxCount = result?.source_tax_count === undefined
    ? sourceTotalFromCategories
    : safeCount(result.source_tax_count);

  return (Array.isArray(result?.verification) ? result.verification : []).map((item) => {
    const counts = item?.counts || {};
    const normalizedCounts = {
      independent: safeCount(counts.independent),
      baserate: safeCount(counts.baserate),
      calculated: safeCount(counts.calculated),
    };
    const categoryTotal = Object.values(normalizedCounts)
      .reduce((total, count) => total + count, 0);
    const totalCount = item?.total_count === undefined
      ? categoryTotal
      : safeCount(item.total_count);
    const expectedCount = sourceTaxCount || totalCount;

    return {
      targetLocationId: String(item?.target_location_id || '').trim(),
      verified: item?.verified === undefined
        ? totalCount === expectedCount
        : Boolean(item.verified),
      totalCount,
      expectedCount,
      counts: normalizedCounts,
      verificationLabel: `${totalCount} of ${expectedCount} verified`,
    };
  }).filter((item) => item.targetLocationId);
};

export const validateTaxCopyResponse = (result, requestedTargetIds) => {
  if (result?.status !== 'success') {
    return { valid: false, message: result?.message || 'The copy request did not return success.' };
  }
  if (result?.verified !== true) {
    return { valid: false, message: result?.message || 'The exact tax copy was not verified.' };
  }

  const requestedIds = [...new Set((Array.isArray(requestedTargetIds) ? requestedTargetIds : [])
    .map((value) => String(value || '').trim()).filter(Boolean))];
  const rows = taxCopyVerificationRows(result);
  const rowsByTarget = new Map(rows.map((row) => [row.targetLocationId, row]));
  const sourceTaxCount = safeCount(result?.source_tax_count);
  const allTargetsVerified = requestedIds.length > 0
    && rows.length === requestedIds.length
    && rowsByTarget.size === requestedIds.length
    && requestedIds.every((targetId) => {
      const row = rowsByTarget.get(targetId);
      return row?.verified === true
        && row.totalCount === sourceTaxCount
        && row.expectedCount === sourceTaxCount;
    });

  return allTargetsVerified
    ? { valid: true, message: '' }
    : {
      valid: false,
      message: result?.message
        || 'Verification did not confirm an exact tax copy for every requested destination.',
    };
};

export const taxCopyVerifiedSummary = (result, sourceLabel, destinationLabels) => {
  const sourceCounts = result?.source_counts || {};
  const destinations = (Array.isArray(destinationLabels) ? destinationLabels : [])
    .filter(Boolean).join(', ');
  return [
    `Source: ${sourceLabel || 'selected source'}.`,
    `Destinations: ${destinations || 'selected destinations'}.`,
    `${safeCount(result?.source_tax_count)} of ${safeCount(result?.source_tax_count)} taxes verified per destination: ${safeCount(sourceCounts.independent)} Fees, ${safeCount(sourceCounts.baserate)} State Taxes, and ${safeCount(sourceCounts.calculated)} Compound Taxes.`,
    `${safeCount(result?.created_count)} added, ${safeCount(result?.updated_count)} updated, and ${safeCount(result?.deleted_count)} destination-only records removed.`,
  ].join(' ');
};

export const taxCopyResultSummary = (result) => {
  const createdCount = safeCount(result?.created_count);
  const updatedCount = safeCount(result?.updated_count);
  const deletedCount = safeCount(result?.deleted_count);
  const locationCount = Array.isArray(result?.target_location_ids)
    ? result.target_location_ids.length
    : 0;

  const operationSummary = [
    `${countLabel(createdCount, 'tax record')} added`,
    `${countLabel(updatedCount, 'tax record')} updated`,
    `${countLabel(deletedCount, 'destination-only tax record')} removed`,
  ].join(', ').replace(/, ([^,]*)$/, ', and $1')
    + ` across ${countLabel(locationCount, 'destination location')}.`;

  if (result?.verified !== true || !result?.source_counts) {
    return operationSummary;
  }

  const sourceTaxCount = safeCount(result.source_tax_count);
  const independentCount = safeCount(result.source_counts.independent);
  const baseRateCount = safeCount(result.source_counts.baserate);
  const calculatedCount = safeCount(result.source_counts.calculated);
  return `${operationSummary} Exact structure verified: ${sourceTaxCount} total taxes per destination (${independentCount} independent, ${baseRateCount} base-rate, ${calculatedCount} calculated).`;
};
